/* eslint-disable camelcase */
// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';

import { UserDO } from 'wdna-components';
import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { messageError, messageInfo } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { getLoggedUserInfo, getAllAreas, updateUserData } from '../../services/user/actions';

import UserInfo from './components/UserInfo';
import UserPassword from './components/UserPassword';
import SkinSwitcher from './components/SkinSwitcher';

type Props = unknown;

interface State {
  loading?: boolean;
  areaList?: any[];

  data?: {
    user?: {
      id?: number;
      name?: string;
      email?: string;
      area_id?: number;
    };
    // Datos extras del usuario
    info?: {
      birthday?: string;
      day?: number;
      month?: number;
      year?: number;
      dni?: string;
      telephone?: string;
      telephoneAlt?: string;
      country?: string;
      province?: string;
      city?: string;
      address?: string;
      cp?: string;
    };
  };
}

export default class UserSettings extends React.Component<Props, State> {
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.mounted = false;

    this.state = {
      loading: false,
      areaList: [],
      data: {
        user: {
          name: '',
          email: '',
        },
        // Datos extras del usuario
        info: {
          birthday: '',
          day: 0,
          month: 0,
          year: 0,
          dni: '',
          telephone: '',
          telephoneAlt: '',
          country: '',
          province: '',
          city: '',
          address: '',
          cp: '',
        }
      },
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load();
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = (): any => {
    getLoggedUserInfo()
      .then((userData: UserDO) => {
        this.parseUserData(userData);
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      });

    getAllAreas()
      .then((areaList: any) => {
        this.setState({
          areaList,
        });
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      });
  }

  public parseUserData = (user: UserDO): void => {
    const { id, email, name, area_id } = user;
    const userIsAdmin: boolean = (user.type === 'adm');
    if (userIsAdmin) {
      const { dni, address, birthday, city, country, cp, province, telephone, telephone_alt } = user.extra || {};
      if (this.mounted) {
        this.setState({
          data: {
            user: { id, email, name, area_id },
            info: {
              birthday,
              day: moment(birthday, 'YYYY-MM-DD').date(),
              month: moment(birthday, 'YYYY-MM-DD').month() + 1,
              year: moment(birthday, 'YYYY-MM-DD').year(),
              dni,
              address,
              city,
              country,
              cp,
              province,
              telephone,
              telephoneAlt: telephone_alt
            }
          }
        });
      }
    }
    if (!userIsAdmin) {
      if (this.mounted) {
        this.setState({
          data: {
            user: { id, email, name, area_id }
          }
        });
      }
    }
  };

  public submit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const { data } = this.state;

    let birthdayOjb: moment.Moment | null = null;
    if (data?.info && data.info.day && data.info.month && data.info.year) {
      birthdayOjb = moment();
      birthdayOjb.set('date', data.info.day);
      birthdayOjb.set('month', data.info.month - 1);
      birthdayOjb.set('year', data.info.year);
      let birthdayStr: string | undefined = birthdayOjb.format('YYYY-MM-DD');
      birthdayStr = moment(birthdayStr).isValid() ? birthdayStr : undefined;
      data.info.birthday = birthdayStr;
    }

    this.sanitizeData(data?.user);
    this.sanitizeData(data?.info);

    this.loadingStart();
    updateUserData(data)
      .then(() => {
        messageInfo('Success', 'Data saved successful', 5000);
        this.load();
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public sanitizeData = (data: any): void => {
    // eslint-disable-next-line no-restricted-syntax
    for (const key in data) {
      if (data[key] === null || data[key] === '') delete data[key];
    }
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;

    const tokens: string[] = name.split('.');
    if (tokens.length === 2) {
      const [outerName, innerName] = tokens;
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          [outerName]: {
            ...prevState.data?.[outerName],
            [innerName]: value,
          },
        }
      }));
    } else {
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          [name]: value,
        }
      }));
    }
  };

  public loadingStart = (): void => {
    this.setState({ loading: true });
  }

  public loadingFinish = (): void => {
    this.setState({ loading: false });
  }

  public render(): JSX.Element {
    const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
    const userIsAdmin: boolean = (user.type === 'adm');

    return (
      <div id="content" className="user-options-panel">
        <WidgetGrid>
          <div className="row">
            <article className="setting_container">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                fullscreenbutton={false}
                colorbutton={false}
                deletebutton={false}
              >
                <header>
                  {userIsAdmin && (
                    <ul className="nav nav-tabs pull-left in secondary-tabs">
                      <li className="active">
                        <a data-toggle="tab" href="#hr1">
                          <i className="fa fa-user" />
                          <span className="hidden-mobile hidden-tablet"> User info </span>
                        </a>
                      </li>
                      <li>
                        <a data-toggle="tab" href="#hr2">
                          <i className="fa fa-lock" />
                          <span className="hidden-mobile hidden-tablet"> User password</span>
                        </a>
                      </li>
                      <li>
                        <a data-toggle="tab" href="#hr3">
                          <i className="fa fa-cogs" />
                          <span className="hidden-mobile hidden-tablet"> Visual options</span>
                        </a>
                      </li>
                    </ul>
                  )}

                  {!userIsAdmin && (
                    <ul className="nav nav-tabs pull-left in secondary-tabs">
                      <li className="active">
                        <a data-toggle="tab" href="#hr2">
                          <i className="fa fa-lock" />
                          <span className="hidden-mobile hidden-tablet"> User password</span>
                        </a>
                      </li>
                      <li>
                        <a data-toggle="tab" href="#hr3">
                          <i className="fa fa-cogs" />
                          <span className="hidden-mobile hidden-tablet"> Visual options</span>
                        </a>
                      </li>
                    </ul>
                  )}

                </header>

                {/* widget content */}
                <div className="widget-body" style={{ padding: 0 }}>

                  {userIsAdmin && (
                    <div className="tab-content">
                      <div className="tab-pane active" id="hr1">
                        <UserInfo
                          submit={this.submit}
                          handleChange={this.handleChange}
                          handleSelect2={this.handleChange}
                          loading={this.state.loading}
                          areaList={this.state.areaList}
                          {...this.state.data}
                        />
                      </div>
                      <div className="tab-pane" id="hr2">
                        <UserPassword />
                      </div>
                      <div className="tab-pane" id="hr3">
                        <SkinSwitcher />
                      </div>
                    </div>
                  )}

                  {!userIsAdmin && (
                    <div className="tab-content">
                      <div className="tab-pane active" id="hr2">
                        <UserPassword />
                      </div>
                      <div className="tab-pane" id="hr3">
                        <SkinSwitcher />
                      </div>
                    </div>
                  )}

                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
